import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import { Link } from "react-router-dom";
import "./ReviewApproval.scss";
import { SkeletonBlock } from "../../Components/Skeleton/Skeleton";
import { showAlert, showConfirm } from "../../feedback/feedbackApi";

const PREVIEW_ROWS = 5;

function IconStethoscope({ className }) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8.5 5.5a2.5 2.5 0 1 1 5 0v1.5a6.5 6.5 0 0 1 5 6.3V15a2 2 0 1 1-4 0v-1.7a2.5 2.5 0 1 0-5 0V15a2 2 0 1 1-4 0v-1.7a6.5 6.5 0 0 1 5-6.3V5.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBuilding({ className }) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 20V8l6-3 6 3v12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9 20v-4h6v4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 12h2M13 12h2M9 16h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconDocument({ className }) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function formatDate(v) {
  if (!v) return "—";
  try {
    const d = typeof v === "string" ? new Date(v) : v;
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
  } catch {
    return "—";
  }
}

function rxDocIsImagePreview(p) {
  if (!p?.fileUrl) return false;
  const isImage = p.fileType && /^image\//i.test(p.fileType);
  const isImageUrl = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(p.fileUrl);
  return !!(isImage || isImageUrl);
}

function documentTypeLabel(type) {
  const t = (type || "").toLowerCase();
  if (t === "report") return "Report";
  if (t === "insurance") return "Insurance";
  if (t === "prescription") return "Prescription";
  return type ? String(type) : "Document";
}

function getItemId(item) {
  return item?._id || item?.id || item?.doctorId || item?.hospitalId || item?.prescriptionId || null;
}

function patientRxGroupKey(p) {
  const uid = p?.userId;
  if (uid != null && String(uid).trim() !== "") return `u:${String(uid)}`;
  const name = (p?.patientName || "").trim().toLowerCase();
  return name ? `n:${name}` : "n:unknown";
}

export default function ReviewApproval() {
  const [summary, setSummary] = useState(null);
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [pendingDegreeEdits, setPendingDegreeEdits] = useState([]);
  const [pendingHospitals, setPendingHospitals] = useState([]);
  const [pendingPrescriptions, setPendingPrescriptions] = useState([]);
  const [totals, setTotals] = useState({
    doctors: 0,
    degreeEdits: 0,
    hospitals: 0,
    prescriptions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [expandDoctors, setExpandDoctors] = useState(false);
  const [expandDegreeEdits, setExpandDegreeEdits] = useState(false);
  const [expandHospitals, setExpandHospitals] = useState(false);
  const [expandRx, setExpandRx] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [rxModalGroupKey, setRxModalGroupKey] = useState(null);

  const loadData = useCallback((opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    const cacheBypass =
      silent === true
        ? { headers: { "Cache-Control": "no-store", Pragma: "no-cache" } }
        : {};

    return Promise.all([
      API.get(endpoints.ADMIN_APPROVAL_SUMMARY, { ...cacheBypass }),
      API.get(endpoints.ADMIN_DOCTORS_PENDING, {
        ...cacheBypass,
        params: { page: 1, limit: 50 },
      }),
      API.get(endpoints.ADMIN_DOCTORS_PENDING_DEGREE_EDITS, {
        ...cacheBypass,
        params: { page: 1, limit: 50 },
      }),
      API.get(endpoints.ADMIN_HOSPITALS_PENDING, {
        ...cacheBypass,
        params: { page: 1, limit: 50 },
      }),
      API.get(endpoints.ADMIN_PENDING_PRESCRIPTIONS, {
        ...cacheBypass,
        params: { page: 1, limit: 50 },
      }),
    ])
      .then(([summaryRes, doctorsRes, degreeEditsRes, hospitalsRes, prescriptionsRes]) => {
        const data = summaryRes?.data?.data || summaryRes?.data || {};
        setSummary(data);

        const docBody = doctorsRes?.data || {};
        const docItems = docBody.items || [];
        setPendingDoctors(docItems);
        const docTotal =
          docBody.total != null ? Number(docBody.total) : docItems.length;

        const degBody = degreeEditsRes?.data || {};
        const degItems = degBody.items || [];
        setPendingDegreeEdits(degItems);
        const degTotal =
          degBody.total != null ? Number(degBody.total) : degItems.length;

        const hospBody = hospitalsRes?.data || {};
        const hospItems = hospBody.items || [];
        setPendingHospitals(hospItems);
        const hospTotal =
          hospBody.total != null ? Number(hospBody.total) : hospItems.length;

        const prescRoot = prescriptionsRes?.data || {};
        const prescList = prescRoot.data ?? prescRoot.items ?? [];
        const prescTotal =
          prescRoot.pagination?.total ??
          data.pendingPrescriptionCount ??
          (Array.isArray(prescList) ? prescList.length : 0);
        setPendingPrescriptions(Array.isArray(prescList) ? prescList : []);

        setTotals({
          doctors: Number.isFinite(docTotal) ? docTotal : 0,
          degreeEdits: Number.isFinite(degTotal) ? degTotal : 0,
          hospitals: Number.isFinite(hospTotal) ? hospTotal : 0,
          prescriptions: Number(prescTotal) || 0,
        });
      })
      .catch((err) => {
        if (!silent) {
          setError(err?.response?.data?.message || err?.message || "Failed to load approval data");
        } else {
          showAlert(err?.response?.data?.message || err?.message || "Could not refresh data.", {
            variant: "error",
            title: "Refresh failed",
          });
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!rxModalGroupKey) return;
    const stillPending = pendingPrescriptions.some((p) => patientRxGroupKey(p) === rxModalGroupKey);
    if (!stillPending) setRxModalGroupKey(null);
  }, [pendingPrescriptions, rxModalGroupKey]);

  useEffect(() => {
    if (!rxModalGroupKey) return;
    const onKey = (e) => {
      if (e.key === "Escape") setRxModalGroupKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rxModalGroupKey]);

  const rxPatientGroups = useMemo(() => {
    const map = new Map();
    for (const p of pendingPrescriptions) {
      const key = patientRxGroupKey(p);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries())
      .map(([key, items]) => {
        const sorted = [...items].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
        const latest = sorted[0]?.createdAt;
        return {
          key,
          patientName: sorted[0]?.patientName || "Patient",
          userId: sorted[0]?.userId,
          items: sorted,
          latestAt: latest ? new Date(latest).getTime() : 0,
        };
      })
      .sort((a, b) => b.latestAt - a.latestAt);
  }, [pendingPrescriptions]);

  const rxModalItems = useMemo(() => {
    if (!rxModalGroupKey) return [];
    return pendingPrescriptions
      .filter((p) => patientRxGroupKey(p) === rxModalGroupKey)
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [pendingPrescriptions, rxModalGroupKey]);

  const rxModalTitle =
    rxModalItems[0]?.patientName || (rxModalGroupKey ? "Patient" : "");

  const approveDoctor = async (id) => {
    setActionKey(`approve-doctor-${id}`);
    try {
      await API.post(endpoints.adminDoctorApprove(id));
      showAlert("Doctor approved.", { variant: "success", title: "Approved" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not approve.", {
        variant: "error",
        title: "Approve failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  const rejectDoctor = async (id) => {
    const ok = await showConfirm("Reject this doctor application?", {
      title: "Reject doctor",
      confirmText: "Reject",
      danger: true,
    });
    if (!ok) return;
    const trimmed = "Rejected by admin review";
    setActionKey(`reject-doctor-${id}`);
    try {
      await API.post(endpoints.adminDoctorReject(id), { rejectionReason: trimmed });
      showAlert("Doctor application rejected.", { variant: "success", title: "Rejected" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not reject.", {
        variant: "error",
        title: "Reject failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  const approveDegreeEdit = async (id) => {
    setActionKey(`approve-degrees-${id}`);
    try {
      await API.post(endpoints.adminDoctorDegreesApprove(id));
      showAlert("Degree changes approved.", { variant: "success", title: "Degrees approved" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not approve degrees.", {
        variant: "error",
        title: "Approve failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  const rejectDegreeEdit = async (id) => {
    const ok = await showConfirm("Reject this doctor's degree update request?", {
      title: "Reject degree update",
      confirmText: "Reject",
      danger: true,
    });
    if (!ok) return;
    setActionKey(`reject-degrees-${id}`);
    try {
      await API.post(endpoints.adminDoctorDegreesReject(id), {
        rejectionReason: "Rejected by admin review",
      });
      showAlert("Degree update rejected.", { variant: "success", title: "Rejected" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not reject degrees.", {
        variant: "error",
        title: "Reject failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  const approveHospital = async (id) => {
    setActionKey(`approve-hospital-${id}`);
    try {
      await API.post(endpoints.adminHospitalApprove(id));
      showAlert("Hospital approved.", { variant: "success", title: "Approved" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not approve.", {
        variant: "error",
        title: "Approve failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  const rejectHospital = async (id) => {
    const ok = await showConfirm("Reject this hospital application?", {
      title: "Reject hospital",
      confirmText: "Reject",
      danger: true,
    });
    if (!ok) return;
    const trimmed = "Rejected by admin review";
    setActionKey(`reject-hospital-${id}`);
    try {
      await API.post(endpoints.adminHospitalReject(id), { rejectionReason: trimmed });
      showAlert("Hospital application rejected.", { variant: "success", title: "Rejected" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not reject.", {
        variant: "error",
        title: "Reject failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  const acceptPrescription = async (id) => {
    setActionKey(`accept-rx-${id}`);
    try {
      await API.post(endpoints.adminAcceptPrescription(id));
      showAlert("Document verified.", { variant: "success", title: "Accepted" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not accept.", {
        variant: "error",
        title: "Accept failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  const rejectPrescription = async (id) => {
    const ok = await showConfirm("Reject this document?", {
      title: "Reject document",
      confirmText: "Reject",
      danger: true,
    });
    if (!ok) return;
    const trimmed = "Rejected by admin review";
    setActionKey(`reject-rx-${id}`);
    try {
      await API.post(endpoints.adminRejectPrescription(id), { rejectionReason: trimmed });
      showAlert("Document rejected.", { variant: "success", title: "Rejected" });
      await loadData({ silent: true });
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not reject.", {
        variant: "error",
        title: "Reject failed",
      });
    } finally {
      setActionKey(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="review_approval_page">
          <div style={{ marginBottom: 14 }}>
            <SkeletonBlock width="35%" height={28} />
          </div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} style={{ marginBottom: 14 }}>
              <SkeletonBlock width="100%" height={110} />
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }
  if (error) {
    return (
      <AdminLayout>
        <div className="review_approval_page">
          <p className="error">{error}</p>
        </div>
      </AdminLayout>
    );
  }

  const doctorMore = Math.max(0, totals.doctors - pendingDoctors.length);
  const degreeEditSlice =
    expandDegreeEdits || pendingDegreeEdits.length <= PREVIEW_ROWS
      ? pendingDegreeEdits
      : pendingDegreeEdits.slice(0, PREVIEW_ROWS);
  const pendingTotal =
    totals.doctors + totals.degreeEdits + totals.hospitals + totals.prescriptions;
  const hospitalMore = Math.max(0, totals.hospitals - pendingHospitals.length);
  const rxMore = Math.max(0, totals.prescriptions - pendingPrescriptions.length);

  const doctorSlice =
    expandDoctors || pendingDoctors.length <= PREVIEW_ROWS
      ? pendingDoctors
      : pendingDoctors.slice(0, PREVIEW_ROWS);
  const hospitalSlice =
    expandHospitals || pendingHospitals.length <= PREVIEW_ROWS
      ? pendingHospitals
      : pendingHospitals.slice(0, PREVIEW_ROWS);
  const rxPatientSlice =
    expandRx || rxPatientGroups.length <= PREVIEW_ROWS
      ? rxPatientGroups
      : rxPatientGroups.slice(0, PREVIEW_ROWS);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData({ silent: true });
      showAlert("Approval queues refreshed.", { variant: "success", title: "Updated" });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <AdminLayout>
      <div className="review_approval_page">
        <header className="review_approval_hero">
          <div className="review_approval_hero__text">
            <h1>Review approval</h1>
            <p className="review_approval_hero__sub">
              Top: decisions still needed. Bottom: recent history (read-only).
            </p>
          </div>
          <button
            type="button"
            className="review_approval_hero__refresh"
            onClick={handleRefresh}
            disabled={refreshing || !!actionKey}
          >
            {refreshing ? "Refreshing..." : "Refresh queues"}
          </button>
          <div className="review_approval_hero__chips" aria-label="Pending queue totals">
            <span className="review_chip review_chip--muted">
              <IconStethoscope className="review_chip__icon" />
              {totals.doctors} doctors
            </span>
            <span className="review_chip review_chip--muted">
              <IconDocument className="review_chip__icon" />
              {totals.degreeEdits} degree updates
            </span>
            <span className="review_chip review_chip--muted">
              <IconBuilding className="review_chip__icon" />
              {totals.hospitals} hospitals
            </span>
            <span className="review_chip review_chip--accent">
              <IconDocument className="review_chip__icon" />
              {totals.prescriptions} uploads
            </span>
            {pendingTotal > 0 && (
              <span className="review_chip review_chip--total">{pendingTotal} total pending</span>
            )}
          </div>
        </header>

        <section className="approval_section" aria-labelledby="pending-heading">
          <div className="approval_section_title">
            <h2 id="pending-heading">Needs your action</h2>
            <span className="status_badge status_badge--pending">Pending</span>
          </div>
          <p className="approval_section_lead">
            Tap a name for the full record. Use Approve/Reject here for speed, or open the dedicated list for files
            and filters.
          </p>
          <div className="approval_grid">
            <div className="approval_card approval_card--doctors">
              <div className="approval_card__head">
                <span className="approval_card__icon" aria-hidden>
                  <IconStethoscope />
                </span>
                <div>
                  <h3>Pending doctors</h3>
                  <p className="approval_card__hint">New applications</p>
                </div>
              </div>
              <p className="count">
                <span className="count__num">{totals.doctors}</span>
              </p>
              <ul
                className={`list${
                  expandDoctors && pendingDoctors.length > PREVIEW_ROWS ? " list--scroll" : ""
                }`}
              >
                {pendingDoctors.length === 0 ? (
                  <li className="empty empty--nice">
                    <span className="empty__title">All clear</span>
                    <span className="empty__sub">No doctor applications waiting.</span>
                  </li>
                ) : (
                  doctorSlice.map((d) => {
                    const rowId = getItemId(d);
                    return (
                    <li key={rowId || d.name} className="approval_row">
                      <Link
                        to={rowId ? `/doctor-list/${encodeURIComponent(rowId)}` : "/doctor-list"}
                        className="approval_row_link"
                      >
                        <span className="approval_row_name">{d.name || "—"}</span>
                        <span className="date">{formatDate(d.createdAt)}</span>
                      </Link>
                      <div className="approval_row_actions">
                        <button
                          type="button"
                          className="approval_btn approval_btn--approve"
                          disabled={!!actionKey || !rowId}
                          onClick={() => rowId && approveDoctor(rowId)}
                        >
                          {actionKey === `approve-doctor-${rowId}` ? "…" : "Approve"}
                        </button>
                        <button
                          type="button"
                          className="approval_btn approval_btn--reject"
                          disabled={!!actionKey || !rowId}
                          onClick={() => rowId && rejectDoctor(rowId)}
                        >
                          {actionKey === `reject-doctor-${rowId}` ? "…" : "Reject"}
                        </button>
                      </div>
                    </li>
                  )})
                )}
              </ul>
              {pendingDoctors.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  className="list_expand_btn"
                  onClick={() => setExpandDoctors((v) => !v)}
                  aria-expanded={expandDoctors}
                >
                  {expandDoctors
                    ? "Show fewer"
                    : `Show all ${pendingDoctors.length} loaded in this card`}
                </button>
              )}
              {doctorMore > 0 && (
                <p className="more">
                  <strong>{doctorMore}</strong> more in the system — open the full queue.
                </p>
              )}
              <Link to="/doctor-list" className="card_link card_link--btn">
                Open doctor queue
              </Link>
            </div>

            <div className="approval_card approval_card--doctors">
              <div className="approval_card__head">
                <span className="approval_card__icon" aria-hidden>
                  <IconDocument />
                </span>
                <div>
                  <h3>Degree updates</h3>
                  <p className="approval_card__hint">Doctor profile changes</p>
                </div>
              </div>
              <p className="count">
                <span className="count__num">{totals.degreeEdits}</span>
              </p>
              <ul
                className={`list${
                  expandDegreeEdits && pendingDegreeEdits.length > PREVIEW_ROWS
                    ? " list--scroll"
                    : ""
                }`}
              >
                {pendingDegreeEdits.length === 0 ? (
                  <li className="empty empty--nice">
                    <span className="empty__title">All clear</span>
                    <span className="empty__sub">No degree updates waiting.</span>
                  </li>
                ) : (
                  degreeEditSlice.map((d) => {
                    const rowId = getItemId(d);
                    const proposed = (d.pendingDegreesEdit?.degrees || [])
                      .map((x) => x.title)
                      .filter(Boolean)
                      .join(", ");
                    return (
                      <li key={rowId || d.name} className="approval_row">
                        <Link
                          to={rowId ? `/doctor-list/${encodeURIComponent(rowId)}` : "/doctor-list"}
                          className="approval_row_link"
                        >
                          <span className="approval_row_name">{d.name || "—"}</span>
                          <span className="date">
                            {proposed || formatDate(d.pendingDegreesEdit?.requestedAt)}
                          </span>
                        </Link>
                        <div className="approval_row_actions">
                          <button
                            type="button"
                            className="approval_btn approval_btn--approve"
                            disabled={!!actionKey || !rowId}
                            onClick={() => rowId && approveDegreeEdit(rowId)}
                          >
                            {actionKey === `approve-degrees-${rowId}` ? "…" : "Approve"}
                          </button>
                          <button
                            type="button"
                            className="approval_btn approval_btn--reject"
                            disabled={!!actionKey || !rowId}
                            onClick={() => rowId && rejectDegreeEdit(rowId)}
                          >
                            {actionKey === `reject-degrees-${rowId}` ? "…" : "Reject"}
                          </button>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
              {pendingDegreeEdits.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  className="approval_card__more"
                  onClick={() => setExpandDegreeEdits((v) => !v)}
                >
                  {expandDegreeEdits
                    ? "Show fewer"
                    : `Show all ${pendingDegreeEdits.length} loaded in this card`}
                </button>
              )}
              <Link to="/doctor-list" className="card_link card_link--btn">
                Open doctor queue
              </Link>
            </div>

            <div className="approval_card approval_card--hospitals">
              <div className="approval_card__head">
                <span className="approval_card__icon" aria-hidden>
                  <IconBuilding />
                </span>
                <div>
                  <h3>Pending hospitals</h3>
                  <p className="approval_card__hint">New applications</p>
                </div>
              </div>
              <p className="count">
                <span className="count__num">{totals.hospitals}</span>
              </p>
              <ul
                className={`list${
                  expandHospitals && pendingHospitals.length > PREVIEW_ROWS ? " list--scroll" : ""
                }`}
              >
                {pendingHospitals.length === 0 ? (
                  <li className="empty empty--nice">
                    <span className="empty__title">All clear</span>
                    <span className="empty__sub">No hospital applications waiting.</span>
                  </li>
                ) : (
                  hospitalSlice.map((h) => {
                    const rowId = getItemId(h);
                    return (
                    <li key={rowId || h.name} className="approval_row">
                      <Link
                        to={`/hospital-list${rowId ? `?highlight=${encodeURIComponent(`hospital-${rowId}`)}` : ""}`}
                        className="approval_row_link"
                      >
                        <span className="approval_row_name">{h.name || "—"}</span>
                        <span className="date">{formatDate(h.createdAt)}</span>
                      </Link>
                      <div className="approval_row_actions">
                        <button
                          type="button"
                          className="approval_btn approval_btn--approve"
                          disabled={!!actionKey || !rowId}
                          onClick={() => rowId && approveHospital(rowId)}
                        >
                          {actionKey === `approve-hospital-${rowId}` ? "…" : "Approve"}
                        </button>
                        <button
                          type="button"
                          className="approval_btn approval_btn--reject"
                          disabled={!!actionKey || !rowId}
                          onClick={() => rowId && rejectHospital(rowId)}
                        >
                          {actionKey === `reject-hospital-${rowId}` ? "…" : "Reject"}
                        </button>
                      </div>
                    </li>
                  )})
                )}
              </ul>
              {pendingHospitals.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  className="list_expand_btn"
                  onClick={() => setExpandHospitals((v) => !v)}
                  aria-expanded={expandHospitals}
                >
                  {expandHospitals
                    ? "Show fewer"
                    : `Show all ${pendingHospitals.length} loaded in this card`}
                </button>
              )}
              {hospitalMore > 0 && (
                <p className="more">
                  <strong>{hospitalMore}</strong> more in the system — open the full queue.
                </p>
              )}
              <Link to="/hospital-list" className="card_link card_link--btn">
                Open hospital queue
              </Link>
            </div>

            <div className="approval_card approval_card--rx approval_card--wide">
              <div className="approval_card__head">
                <span className="approval_card__icon" aria-hidden>
                  <IconDocument />
                </span>
                <div>
                  <h3>Prescriptions &amp; reports</h3>
                  <p className="approval_card__hint">Patient uploads to verify</p>
                </div>
              </div>
              <p className="count">
                <span className="count__num">{totals.prescriptions}</span>
              </p>
              <ul
                className={`list list--rx${
                  expandRx && rxPatientGroups.length > PREVIEW_ROWS ? " list--scroll" : ""
                }`}
              >
                {rxPatientGroups.length === 0 ? (
                  <li className="empty empty--nice">
                    <span className="empty__title">All clear</span>
                    <span className="empty__sub">No documents waiting for verification.</span>
                  </li>
                ) : (
                  rxPatientSlice.map((g) => {
                    const n = g.items.length;
                    const summary =
                      n === 1 ? "1 document to verify" : `${n} documents to verify`;
                    return (
                      <li key={g.key} className="approval_row approval_row--rx_summary">
                        <button
                          type="button"
                          className="approval_row_rx_trigger"
                          onClick={() => setRxModalGroupKey(g.key)}
                          disabled={!!actionKey}
                        >
                          <span className="approval_row_name">
                            {g.patientName}
                            <span className="approval_row_rx_meta"> · {summary}</span>
                          </span>
                          <span className="date">{formatDate(g.items[0]?.createdAt)}</span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
              {rxPatientGroups.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  className="list_expand_btn"
                  onClick={() => setExpandRx((v) => !v)}
                  aria-expanded={expandRx}
                >
                  {expandRx
                    ? "Show fewer"
                    : `Show all ${rxPatientGroups.length} patients loaded in this card`}
                </button>
              )}
              {rxMore > 0 && (
                <p className="more">
                  <strong>{rxMore}</strong> more in the system (not loaded here) — open the full list.
                </p>
              )}
              <Link to="/prescriptions-reports" className="card_link card_link--btn">
                Open prescriptions &amp; reports
              </Link>
            </div>
          </div>
        </section>

        <section className="approval_section approval_section--history" aria-labelledby="recent-heading">
          <div className="approval_section_title">
            <h2 id="recent-heading">Recently approved</h2>
            <span className="status_badge status_badge--done">History</span>
          </div>
          <p className="approval_section_lead">A quick log of the latest approvals — for reference only.</p>
          <div className="approval_grid">
            <div className="approval_card approved">
              <h3>Recently approved doctors</h3>
              <ul className="list">
                {!summary?.recentlyApprovedDoctors || summary.recentlyApprovedDoctors.length === 0 ? (
                  <li className="empty">None yet.</li>
                ) : (
                  summary.recentlyApprovedDoctors.map((d) => (
                    <li key={d._id}>
                      <span>{d.name}</span>
                      <span className="date">{formatDate(d.updatedAt || d.createdAt)}</span>
                    </li>
                  ))
                )}
              </ul>
            </div>
            <div className="approval_card approved">
              <h3>Recently approved hospitals</h3>
              <ul className="list">
                {!summary?.recentlyApprovedHospitals || summary.recentlyApprovedHospitals.length === 0 ? (
                  <li className="empty">None yet.</li>
                ) : (
                  summary.recentlyApprovedHospitals.map((h) => (
                    <li key={h._id}>
                      <span>{h.name}</span>
                      <span className="date">{formatDate(h.updatedAt || h.createdAt)}</span>
                    </li>
                  ))
                )}
              </ul>
            </div>
            <div className="approval_card approved">
              <h3>Recently verified (patient documents)</h3>
              <ul className="list">
                {!summary?.recentlyAcceptedPrescriptions || summary.recentlyAcceptedPrescriptions.length === 0 ? (
                  <li className="empty">None yet.</li>
                ) : (
                  summary.recentlyAcceptedPrescriptions.map((p) => (
                    <li key={p._id}>
                      <span>
                        {p.patientName} – {p.type}
                      </span>
                      <span className="date">{formatDate(p.updatedAt || p.createdAt)}</span>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </section>

        {rxModalGroupKey && (
          <div
            className="approval_rx_modal_backdrop"
            role="presentation"
            onClick={() => setRxModalGroupKey(null)}
          >
            <div
              className="approval_rx_modal_panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="approval-rx-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="approval_rx_modal_header">
                <div>
                  <h2 id="approval-rx-modal-title" className="approval_rx_modal_title">
                    {rxModalTitle}
                  </h2>
                  <p className="approval_rx_modal_sub">
                    {rxModalItems.length === 1
                      ? "1 document to verify"
                      : `${rxModalItems.length} documents to verify`}
                  </p>
                </div>
                <button
                  type="button"
                  className="approval_rx_modal_close"
                  onClick={() => setRxModalGroupKey(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <ul className="approval_rx_modal_list list list--rx">
                {rxModalItems.map((p) => {
                  const rowId = getItemId(p);
                  const isImg = rxDocIsImagePreview(p);
                  const openFile = () => {
                    if (!p.fileUrl) return;
                    window.open(p.fileUrl, "_blank", "noopener,noreferrer");
                  };
                  return (
                    <li
                      key={rowId || `${p.patientName}-${p.createdAt}`}
                      className="approval_row approval_rx_modal_doc_row"
                    >
                      <div className="approval_rx_modal_doc_start">
                        <button
                          type="button"
                          className="approval_rx_modal_thumb_btn"
                          onClick={openFile}
                          disabled={!p.fileUrl}
                          aria-label={isImg ? "Open image preview" : "Open file"}
                          title={p.fileUrl ? "Open file" : "No file URL"}
                        >
                          {p.fileUrl && isImg ? (
                            <img
                              src={p.fileUrl}
                              alt=""
                              className="approval_rx_modal_thumb"
                              loading="lazy"
                            />
                          ) : (
                            <span className="approval_rx_modal_thumb_placeholder">
                              {p.fileType?.toLowerCase().includes("pdf") ? "PDF" : p.fileUrl ? "File" : "—"}
                            </span>
                          )}
                        </button>
                        <Link
                          to={`/prescriptions-reports${rowId ? `?highlight=${encodeURIComponent(`prescription_${rowId}`)}` : ""}`}
                          className="approval_row_link approval_rx_modal_doc_link"
                        >
                          <span className="approval_row_name approval_rx_modal_doc_name_block">
                            <span className="approval_rx_modal_doc_title">
                              {documentTypeLabel(p.type)}
                              {p.originalName ? ` · ${p.originalName}` : ""}
                            </span>
                            {p.requestCategorySubcategory ? (
                              <span className="approval_rx_modal_req_cat" title={p.requestCategorySubcategory}>
                                Request · {p.requestCategorySubcategory}
                              </span>
                            ) : null}
                          </span>
                          <span className="date">{formatDate(p.createdAt)}</span>
                        </Link>
                      </div>
                      <div className="approval_row_actions">
                        <button
                          type="button"
                          className="approval_btn approval_btn--approve"
                          disabled={!!actionKey || !rowId}
                          onClick={() => rowId && acceptPrescription(rowId)}
                        >
                          {actionKey === `accept-rx-${rowId}` ? "…" : "Accept"}
                        </button>
                        <button
                          type="button"
                          className="approval_btn approval_btn--reject"
                          disabled={!!actionKey || !rowId}
                          onClick={() => rowId && rejectPrescription(rowId)}
                        >
                          {actionKey === `reject-rx-${rowId}` ? "…" : "Reject"}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
